import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  findPickerSelectionForTime,
  isConfirmedPickupInPicker,
} from './action-pickup-time-selector/earliest-pickup-time';
import {
  type UseDatePickerOptionsResult,
  useDatePickerOptions,
} from './action-pickup-time-selector/use-date-time-picker-options';
import { useCheckoutPickupTimeDisplayTick } from './action-pickup-time-selector/use-pickup-slot-refresh-tick';

type PickupTimeOptionsContextValue = UseDatePickerOptionsResult & {
  /** Bumps every minute so checkout label and offer pickUpTime recompute against now. */
  displayRefreshTick: number;
  /** User-confirmed slot from the modal; cleared when repricing or the slot is no longer offered. */
  confirmedPickupTime: Date | undefined;
  setConfirmedPickupTime: (time: Date) => void;
  /** confirmedPickupTime ?? defaultPickupTime — never derived from fireOrderIn */
  displayPickupTime: Date | undefined;
  /** False after first modal confirm; reset on repricing so ASAP can show again on a fresh price. */
  allowAsapOnCheckoutSummary: boolean;
};

const PickupTimeOptionsContext = createContext<PickupTimeOptionsContextValue | null>(null);

export function PickupTimeOptionsProvider({ children }: { children: ReactNode }) {
  const pickerOptions = useDatePickerOptions();
  const displayRefreshTick = useCheckoutPickupTimeDisplayTick();
  const { defaultPickupTime, pickupScheduleKey } = pickerOptions;
  const [confirmedPickupTime, setConfirmedPickupTime] = useState<Date>();
  const [allowAsapOnCheckoutSummary, setAllowAsapOnCheckoutSummary] = useState(true);
  const previousPickupScheduleKeyRef = useRef<string | null>(null);

  // Reset only when repricing returns a new server schedule — not on mount or 5-minute refresh.
  useEffect(() => {
    if (
      previousPickupScheduleKeyRef.current !== null &&
      previousPickupScheduleKeyRef.current !== pickupScheduleKey
    ) {
      setConfirmedPickupTime(undefined);
      setAllowAsapOnCheckoutSummary(true);
    }
    previousPickupScheduleKeyRef.current = pickupScheduleKey;
  }, [pickupScheduleKey]);

  // Clear only when the confirmed slot is no longer in the filtered picker list.
  useEffect(() => {
    if (!confirmedPickupTime || !pickerOptions.datePickerData?.length) {
      return;
    }

    if (!isConfirmedPickupInPicker(pickerOptions.datePickerData, confirmedPickupTime)) {
      setConfirmedPickupTime(undefined);
    }
  }, [confirmedPickupTime, pickerOptions.datePickerData, pickerOptions.slotRefreshTick]);

  const displayPickupTime = confirmedPickupTime ?? defaultPickupTime;

  const value = useMemo(
    () => ({
      ...pickerOptions,
      displayRefreshTick,
      confirmedPickupTime,
      setConfirmedPickupTime: (time: Date) => {
        const matched = findPickerSelectionForTime(pickerOptions.datePickerData, time);
        setConfirmedPickupTime(
          matched ? new Date(matched.slotIso) : time
        );
        setAllowAsapOnCheckoutSummary(false);
      },
      displayPickupTime,
      allowAsapOnCheckoutSummary,
    }),
    [
      pickerOptions,
      displayRefreshTick,
      confirmedPickupTime,
      displayPickupTime,
      allowAsapOnCheckoutSummary,
    ]
  );

  return (
    <PickupTimeOptionsContext.Provider value={value}>
      {children}
    </PickupTimeOptionsContext.Provider>
  );
}

export function usePickupTimeOptions(): PickupTimeOptionsContextValue {
  const context = useContext(PickupTimeOptionsContext);
  if (!context) {
    throw new Error('usePickupTimeOptions must be used within PickupTimeOptionsProvider');
  }
  return context;
}
